import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { AboveAppProgressModalContainer } from '@components/AppProgressOverlay/AboveAppProgress/AboveAppProgress'
import { popupToast } from '@components/Notifications/popupToast'
import { dismissToast, StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { minterRoleId } from '@components/SpaceSettingsPanel/rolePermissions.const'
import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { GatedTownModal } from '@components/Web3/GatedTownModal/GatedTownModal'
import { useMyMembershipStatus } from '@components/Web3/RenewMembership/hooks/useMyMembershipStatus'
import { useRenewMyMembership } from '@components/Web3/RenewMembership/hooks/useRenewMyMembership'
import { isAddress } from '@components/Web3/Wallet/useGetWalletParam'
import { isSpaceStreamId, Membership } from '@towns-protocol/sdk'
import { Box, BoxProps, FancyButton, Icon, IconProps, Stack, Text } from '@ui'
import { trackError } from 'hooks/useAnalytics'
import { useDevice } from 'hooks/useDevice'
import { useEntitlements } from 'hooks/useEntitlements'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { PATHS } from 'routes'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    AuthStatus,
    Permission,
    SpaceIdFromSpaceAddress,
    useConnectivity,
    useContractSpaceInfoWithoutClient,
    useHasPermission,
    useSpaceDataWithId,
    useTownsContext,
    useWalletAddressIsBanned,
} from 'use-towns-client'

import { usePublicPageLoginFlow } from './usePublicPageLoginFlow'

const LoginComponent = React.lazy(() => import('@components/Login/LoginComponent'))

export function JoinLoginButton({
    leftContent,
    spaceId,
    horizontal = false,
}: {
    leftContent?: React.ReactNode
    spaceId: string | undefined
    horizontal?: boolean
}) {
    const { isAuthenticated, loggedInWalletAddress } = useConnectivity()
    const { clientSingleton, signerContext } = useTownsContext()
    const { data: spaceInfo } = useContractSpaceInfoWithoutClient(spaceId)
    const { enableUsdc } = useFeatureFlags()

    const {
        startJoinMeetsRequirements,
        startJoinPreLogin,
        endPublicPageLoginFlow,
        startJoinDoesNotMeetRequirements,
        spaceBeingJoined,
        disableJoinUi,
        assetModal,
        setAssetModal,
    } = usePublicPageLoginFlow()

    const { isTouch } = useDevice()
    const hideAssetModal = ({ shouldEndLoginFlow }: { shouldEndLoginFlow: boolean }) => {
        if (shouldEndLoginFlow) {
            endPublicPageLoginFlow()
        }
        setAssetModal(false)
    }

    const { hasPermission: meetsMembershipRequirements, isLoading: isLoadingMeetsMembership } =
        useHasPermission({
            spaceId: spaceId,
            walletAddress: loggedInWalletAddress,
            permission: Permission.JoinSpace,
        })

    const { clickedJoinTownOnTownPage: clickedJoinTown, viewedGatedTownRequirementsModal } =
        useJoinFunnelAnalytics()

    const { data: entitlements } = useEntitlements(spaceId, minterRoleId)

    const { data: membershipStatus, isLoading: isLoadingMembershipStatus } = useMyMembershipStatus({
        spaceId,
    })
    const hasMemberNft = membershipStatus?.isMember
    const isMembershipExpired = membershipStatus?.isExpired

    // Calculate maxSupplyReached from membership info
    const { data: membershipInfo, isLoading: isLoadingMembershipInfo } = useReadableMembershipInfo(
        spaceId ?? '',
    )
    const { totalSupply, maxSupply } = membershipInfo ?? {}
    const seatsLeft =
        typeof maxSupply === 'number' && typeof totalSupply === 'number'
            ? maxSupply - totalSupply
            : undefined
    const maxSupplyReached = isLoadingMembershipInfo
        ? false
        : seatsLeft !== undefined && seatsLeft === 0

    const streamId = useMemo(() => {
        if (!spaceId) {
            return undefined
        }
        return isAddress(spaceId)
            ? SpaceIdFromSpaceAddress(spaceId)
            : isSpaceStreamId(spaceId)
              ? spaceId
              : undefined
    }, [spaceId])

    const space = useSpaceDataWithId(streamId, 'JoinLoginButton')
    const isMember = space?.membership === Membership.Join

    const { isBanned, isLoading: isBannedLoading } = useWalletAddressIsBanned(
        spaceId,
        loggedInWalletAddress,
        {
            skipCache: true,
        },
    )

    const blockJoining = useMemo(() => {
        if (isLoadingMembershipStatus || isLoadingMembershipInfo || hasMemberNft) {
            return false
        }
        return maxSupplyReached
    }, [hasMemberNft, isLoadingMembershipStatus, isLoadingMembershipInfo, maxSupplyReached])

    const disableOutdatedEthSpace = enableUsdc && membershipInfo?.currency === 'ETH'

    const joinOrPriceText = useMemo(() => {
        if (isMembershipExpired) {
            return 'Renew Membership'
        }
        if (isMember) {
            return 'Open Town'
        }
        if (hasMemberNft) {
            return 'Rejoin'
        }
        if (entitlements?.hasEntitlements) {
            return 'Verify Assets to Join'
        }
        return 'Join Town'
    }, [entitlements?.hasEntitlements, hasMemberNft, isMembershipExpired, isMember])

    const { isLoading: isLoadingRenewMembership, renewMembership } = useRenewMyMembership({
        location: 'town-page',
    })

    const onRenewMembershipClick = useCallback(
        (getSigner: GetSigner) => {
            renewMembership(getSigner, 'Renew Membership')
        },
        [renewMembership],
    )

    const navigate = useNavigate()

    const onClickAsMember = useCallback(() => {
        navigate(`/${PATHS.SPACES}/${spaceId}/`)
    }, [navigate, spaceId])

    // user is already signed in, clicking join button
    const onJoinClick = useCallback(
        async (getSigner: GetSigner) => {
            if (disableJoinUi || disableOutdatedEthSpace) {
                return
            }
            const signer = await getSigner()

            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }
            const { pricingModule } = getCachedSpaceDataForAnalytics()
            clickedJoinTown({
                meetsMembershipRequirements,
                spaceId,
                pricingModule,
                authenticated: true,
            })
            if (hasMemberNft || meetsMembershipRequirements) {
                startJoinMeetsRequirements({
                    signer,
                    clientSingleton,
                    signerContext,
                    source: 'public pagejoin click',
                    analyticsData: {
                        spaceName: spaceInfo?.name ?? '',
                    },
                })
            } else {
                // show asset verification modal
                viewedGatedTownRequirementsModal({
                    spaceId,
                    meetsMembershipRequirements: !!meetsMembershipRequirements,
                })
                startJoinDoesNotMeetRequirements()
            }
        },
        [
            disableJoinUi,
            clickedJoinTown,
            meetsMembershipRequirements,
            spaceId,
            hasMemberNft,
            startJoinMeetsRequirements,
            clientSingleton,
            signerContext,
            spaceInfo?.name,
            viewedGatedTownRequirementsModal,
            startJoinDoesNotMeetRequirements,
            disableOutdatedEthSpace,
        ],
    )

    // user is not signed in, clicking join button
    const onLoginClick = useCallback(() => {
        if (blockJoining) {
            return
        }
        const { pricingModule } = getCachedSpaceDataForAnalytics()
        clickedJoinTown({ spaceId, pricingModule, authenticated: false })
        startJoinPreLogin()
    }, [startJoinPreLogin, blockJoining, spaceId, clickedJoinTown])

    const isEvaluating = useWatchEvaluatingCredentialsAuthStatus()

    const content = () => {
        if (isBanned && !isBannedLoading) {
            return null
        }
        if (isEvaluating) {
            return (
                <LoadingStatusMessage spinner background="level2" message="Connecting to Towns" />
            )
        }
        if (disableOutdatedEthSpace) {
            return (
                <Box padding={isTouch ? 'md' : undefined}>
                    <Text fontSize={isTouch ? 'sm' : undefined}>
                        Joining is suspended until the space is updated to accept USDC.
                    </Text>
                </Box>
            )
        }
        if (!isAuthenticated) {
            return (
                <Box width={isTouch ? undefined : '300'}>
                    <LoginComponent
                        text={
                            entitlements?.hasEntitlements ? (
                                <Box horizontal centerContent gap="sm">
                                    <Icon type="lock" size="square_xs" />
                                    {joinOrPriceText}
                                </Box>
                            ) : (
                                joinOrPriceText
                            )
                        }
                        onLoginClick={onLoginClick}
                    />
                </Box>
            )
        }

        if (isLoadingMeetsMembership) {
            return (
                <LoadingStatusMessage spinner background="level2" message="Checking membership" />
            )
        }

        return isMember && !isMembershipExpired ? (
            <FancyButton cta onClick={onClickAsMember}>
                {joinOrPriceText}
            </FancyButton>
        ) : (
            <WalletReady withPrimaryWallet>
                {({ getSigner }) => (
                    <Box tooltip={blockJoining ? 'No memberships left' : undefined}>
                        <FancyButton
                            cta={!isMembershipExpired}
                            {...(isMembershipExpired ? { background: 'negativeSubtle' } : {})}
                            type="button"
                            disabled={disableJoinUi || blockJoining}
                            spinner={!!spaceBeingJoined || isLoadingRenewMembership}
                            onClick={() => {
                                if (isMembershipExpired) {
                                    onRenewMembershipClick(getSigner)
                                } else {
                                    onJoinClick(getSigner)
                                }
                            }}
                        >
                            {
                                (entitlements?.hasEntitlements && !hasMemberNft ? (
                                    <Box horizontal centerContent gap="sm">
                                        <Icon type="lock" size="square_xs" />
                                        {joinOrPriceText}
                                    </Box>
                                ) : (
                                    joinOrPriceText
                                )) as string
                            }
                        </FancyButton>
                    </Box>
                )}
            </WalletReady>
        )
    }

    return (
        <>
            {isTouch ? (
                <Stack centerContent horizontal={horizontal} gap="xs">
                    {leftContent ? (
                        <Box grow centerContent minHeight="x5">
                            {leftContent}
                        </Box>
                    ) : null}
                    <Box grow>{content()}</Box>
                </Stack>
            ) : (
                <Stack gap>
                    {leftContent ? <Box grow>{leftContent}</Box> : null}
                    <Box grow>{content()}</Box>
                </Stack>
            )}

            {spaceId && assetModal && (
                <AboveAppProgressModalContainer
                    padding="none"
                    minWidth="350"
                    background="none"
                    onHide={() => hideAssetModal({ shouldEndLoginFlow: true })}
                >
                    <GatedTownModal spaceId={spaceId} onHide={hideAssetModal} />
                </AboveAppProgressModalContainer>
            )}
        </>
    )
}

function useWatchEvaluatingCredentialsAuthStatus() {
    const { authStatus } = useConnectivity()

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined
        let toastId: string | undefined
        let isCurrentEffect = true

        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
                timeoutId = undefined
            }
            if (toastId) {
                dismissToast(toastId)
                toastId = undefined
            }
        }

        if (authStatus === AuthStatus.EvaluatingCredentials) {
            timeoutId = setTimeout(() => {
                if (!isCurrentEffect) {
                    return
                }
                const message =
                    '[useWatchEvaluatingCredentialsAuthStatus] timeout connecting to Towns'
                trackError({
                    error: new Error(message),
                    category: 'river',
                    displayText: message,
                    code: 'timeout',
                    source: 'credentials auth status public page join',
                })
                toastId = popupToast(
                    ({ toast }) => (
                        <StandardToast.Error
                            message={`We're having trouble connecting with Towns. Please try again later.`}
                            toast={toast}
                        />
                    ),
                    {
                        duration: Infinity,
                    },
                )
            }, 15_000)
        } else {
            cleanup()
        }

        return () => {
            isCurrentEffect = false
            cleanup()
        }
    }, [authStatus])

    return authStatus === AuthStatus.EvaluatingCredentials
}
const LoadingStatusMessage = ({
    background = 'error',
    message,
    icon,
    spinner,
}: {
    background?: BoxProps['background']
    message: string
    icon?: IconProps['type']
    spinner?: boolean
}) => {
    return (
        <Box
            horizontal
            centerContent
            gap
            rounded="sm"
            background={background}
            width="100%"
            maxWidth="300"
            height="x6"
            padding="lg"
        >
            {spinner && <ButtonSpinner />}
            {icon && <Icon type={icon} />}
            <Text>{message}</Text>
        </Box>
    )
}
